import { Alert, Box, Link } from "@mui/material";
import { useDeepCompareMemo } from "@react-hookz/web";
import { isEqual } from "lodash-es";
import type { FC } from "react";
import { makeDefaultTemplateValues } from "../../../../email-design/components/email-design";
import type { EditorModalState } from "../../lexical-context";
import { useLexicalToolbar } from "../toolbar/hooks/use-lexical-toolbar";
import { ToolbarV2 } from "../toolbar/toolbar-wrapper-v2";
import { ToolbarElement } from "./toolbar-element";
import type { EmailTemplateSelectorSettings, GenericToolbarElement } from "./types";

export type GenericToolbarProps = {
  disabled?: boolean;
  channel: "slack" | "teams" | "email" | "text";
  topLevelElements: GenericToolbarElement[];
  rightAlignedElements?: GenericToolbarElement[];
  openModal: (data: EditorModalState) => void;
  stickyTop?: number;
};

export const GenericToolbar: FC<GenericToolbarProps> = ({
  channel,
  disabled,
  topLevelElements,
  rightAlignedElements,
  openModal,
  stickyTop,
}) => {
  const toolbarState = useLexicalToolbar(channel);

  return (
    <>
      {channel === "email" && (
        <DefaultTemplateAlert
          settings={
            topLevelElements.find((element) => element.type === "emailTemplateSelector")?.settings
          }
        />
      )}
      <ToolbarV2
        disabled={disabled}
        mode="toolbar"
        stickyTop={stickyTop}
        rightAlignedElements={
          <Box>
            {rightAlignedElements?.map((element, index) => (
              <ToolbarElement
                element={element}
                key={index}
                disabled={disabled}
                toolbarState={toolbarState}
                openModal={openModal}
                mode="toolbar"
              />
            ))}
          </Box>
        }
      >
        {topLevelElements.map((element, index) => (
          <ToolbarElement
            element={element}
            key={index}
            disabled={disabled}
            toolbarState={toolbarState}
            openModal={openModal}
            mode="toolbar"
          />
        ))}
      </ToolbarV2>
    </>
  );
};

const DefaultTemplateAlert: FC<{ settings?: EmailTemplateSelectorSettings }> = ({ settings }) => {
  const isDefaultTemplate = useDeepCompareMemo(() => {
    if (!settings || settings.emailDesigns.length > 1) {
      return false;
    }

    const defaultValue = makeDefaultTemplateValues();
    const compareProps = [
      "button_settings",
      "layout_settings",
      "text_settings",
      "divider_settings",
      "header",
      "footer",
    ] as (keyof typeof defaultValue)[];

    return compareProps.every((prop) => {
      const current = settings?.selectedEmailDesign?.[prop];
      return !current || Object.keys(current).length === 0 || isEqual(defaultValue[prop], current);
    });
  }, [settings]);

  if (!isDefaultTemplate) return null;

  return (
    <Alert severity="info">
      This Communication is using the default email template. Consider customizing your email
      template settings <Link href="/manage/brand/email_design/header">here</Link>.
    </Alert>
  );
};
